import { useEffect, useRef, useState } from 'react'
import { teachableTerm } from '../lib/synonymTeaching.ts'
import type { Finding, Review } from '../lib/api.ts'
import { CHECK_LABEL } from '../lib/vocabulary.ts'
import { Button } from './Button.tsx'
import { Icon } from './Icon.tsx'
import { FOCUS_RING } from './focusRing.ts'
import { Input } from './Input.tsx'
import { Tooltip } from './Tooltip.tsx'

/**
 * The two decisions, on the row that carries the divergence.
 *
 * **Where they belong.** The first version put a pair of labelled buttons
 * under the whole preparation section — the wrong size and the wrong granularity
 * at once: a preparation can disagree on the form, the posology and each
 * ingredient separately, and one control beneath all of them cannot say which
 * one it answered. These sit in the row's third column, beside the pin, on the
 * row whose difference they are about.
 *
 * **Icons, with the words in the tooltip.** The labels are the point of the
 * vocabulary and too long to repeat on every row; a ✓ and an ✗ that name
 * themselves on hover keep the row's height unchanged, which is what makes a
 * list of eight differences readable at all.
 *
 * **The reason is a popover, not a panel.** Asking why we were wrong must not
 * push the row it belongs to off the screen — the pharmacist is answering a
 * question *about* that difference and needs to keep reading it.
 */

/** The divergence stands, and the quotation has to change. */
const WILL_FIX = 'corrected'
/**
 * The quotation was already right.
 *
 * Stored as `accepted` — literally "aceitar o orçamento" — rather than
 * `rejected`, and it is the only decision that can teach the catalogue.
 */
export const NOT_A_DIVERGENCE = 'accepted'

/**
 * Small, but unmistakably buttons.
 *
 * Bare icons read as status rather than as controls -- people looked at a ✓
 * and saw a decision already given. A border, a background on hover and a real
 * hit area say "press me" at a size that still leaves the row's height alone.
 */
const RAISED =
  'inline-flex h-6 w-7 items-center justify-center rounded-[5px] border bg-fill-white text-[11px] ' +
  'transition-colors disabled:opacity-40 ' +
  FOCUS_RING

/** The same button, held down. */
const PRESSED =
  'inline-flex h-6 w-7 items-center justify-center rounded-[5px] border text-[11px] shadow-inner ' +
  'disabled:opacity-40 ' +
  FOCUS_RING

export interface DecisionActionsProps {
  readonly finding: Finding
  /**
   * The other findings the same press answers.
   *
   * An ativo row is one line of the orçamento and one question -- "esta linha
   * está certa?" -- but the comparison can flag it along more than one
   * dimension at once: `Fluoxetina 10 mg` keyed as `CLOREXIDINA 2 %` is an
   * unresolved identity *and* an incomparable dose (orçamento 836739). Drawn a
   * control apiece, that row carried two identical unlabelled ✓/✗ pairs with
   * nothing to say which was which -- the failure this component's own note
   * above argues against -- and the keyboard, which clicks the first
   * `[data-decision]` it finds in the row, answered one and left the other
   * outstanding.
   *
   * So the row gets one control, and it answers all of them. Each finding is
   * still recorded on its own fingerprint, so what was decided per dimension
   * is unchanged; only the question on screen is one.
   */
  readonly alongside?: readonly Finding[]
  readonly recorded: Review | undefined
  readonly busy: boolean
  /**
   * A row nothing flagged. Offers only "marcar divergência": the pharmacist
   * saw something the comparison did not, and the quotation has to change.
   * "Não é divergência" would be answering a question nobody asked.
   */
  readonly quiet?: boolean
  readonly onDecision: (
    fingerprint: string,
    decision: string,
    options?: {
      synonymTerm?: string | undefined
      notes?: string | undefined
      /** Why, in Galdino's wording. See `REASONS`. */
      reason?: string | undefined
    },
  ) => void
  readonly onClear: (fingerprint: string) => void
  /**
   * Offered only on a keyed line the receita never named: "É excipiente",
   * the override of the ERP's type for that product (ADR 0014). Choosing it
   * hands the row's fingerprints to the caller, which confirms, marks the
   * product and then answers them with it. Absent, the reason is
   * simply not on the list.
   *
   * Every fingerprint the control speaks for, not just the first: the mark is
   * declinable, so the answers cannot be recorded here before the caller knows
   * whether it went through.
   */
  readonly onExcipient?: ((fingerprints: readonly string[]) => void) | undefined
  /** The receita's name on this row, which a dose finding needs to teach a synonym. */
  readonly rowName?: string | undefined
  /**
   * The two answers as words, each saying what it means (#641).
   *
   * For the divergence-only pass, where a step is one divergence and there is
   * room to say it: the icons above exist to keep a row of twenty short, and
   * a step of one has no such row. Same answers, same reason question, same
   * `data-decision` hooks for the keys.
   */
  readonly labelled?: boolean
}

/**
 * Why a pharmacist disagrees, in Galdino's wording.
 *
 * The order is how often each is true, most first: our own reading is the
 * commonest cause and the one worth counting, and "Outros" is last because
 * reaching it means the other three did not fit.
 */
const REASONS: ReadonlyArray<{ readonly id: string; readonly label: string }> = [
  { id: 'ai-misread', label: 'A IA leu errado' },
  { id: 'prescriber', label: 'Prescritor(a) escreveu errado' },
  { id: 'synonym', label: 'É um sinônimo' },
  { id: 'other', label: 'Outros (descrever)' },
]

/**
 * The line is an excipient the ERP keyed as an ingredient.
 *
 * Kept out of `REASONS`: those are answered here, and this one is two things
 * at once -- an override of the product's type for every later conferência,
 * and the reason this finding is not a divergence. The caller does both, in
 * that order, so the mark is never recorded without the answer it justifies.
 */
const EXCIPIENT = { id: 'excipient', label: 'É excipiente' } as const

/**
 * What the lone ✗ says and records, by what it sits on.
 *
 * On a comparison that flagged nothing it reports something we missed, and the
 * orçamento goes for correction. On an operational reminder (#549) it is the
 * ✗ of every other apontamento instead: the rule pointed at something that is
 * not so in this case. A lembrete asks for no answer, so the one thing worth
 * telling us about it is that it should not have fired -- and "corrigir" on
 * it said the opposite of what ✗ means everywhere else on the screen.
 *
 * Recorded with no reason: the four on offer are about a reading of the
 * receita, and none of them is what went wrong with a rule.
 */
const QUIET = {
  missed: {
    label: 'Marcar divergência',
    means:
      'Marcar divergência — algo está errado que a comparação não apontou; o orçamento será corrigido.',
    decision: WILL_FIX,
    hook: 'fix',
    /*
     * Shown only while the row is pointed at or focused.
     *
     * A red ✗ standing on every row that matched read as something being
     * wrong with it, on the rows where nothing is. It is an offer, so it
     * appears where somebody is looking -- and stays clickable, since opacity
     * hides it without taking it out of the row.
     */
    reveal:
      'opacity-0 transition-opacity focus-visible:opacity-100 in-[[data-review-row]:hover]:opacity-100 in-[[data-review-row]:focus-within]:opacity-100',
  },
  reminder: {
    label: 'Não se aplica',
    means: 'Não se aplica — a regra apontou errado neste caso.',
    decision: NOT_A_DIVERGENCE,
    // No hook: the keyboard answers a row by pressing the first ✓ or ✗ in
    // it, and a reminder is never the thing a row is asking about.
    hook: undefined,
    // A reminder is on screen already, so the answer to it stays in view.
    reveal: '',
  },
} as const

const quietFor = (finding: Finding) =>
  finding.check === 'operational' ? QUIET.reminder : QUIET.missed

/** The same words, for a screen that shows a reason somebody already gave. */
export const REASON_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  [...REASONS, EXCIPIENT].map((reason) => [reason.id, reason.label]),
)

export function DecisionActions({
  finding,
  alongside = [],
  recorded,
  busy,
  quiet = false,
  onDecision,
  onClear,
  onExcipient,
  rowName,
  labelled = false,
}: DecisionActionsProps) {
  /** Every finding this one control answers, the one it speaks for first. */
  const answering = [finding, ...alongside]
  const teaches = canTeach(finding, rowName)
  const [asking, setAsking] = useState(false)
  const [term, setTerm] = useState('')
  const [notes, setNotes] = useState('')
  /*
   * Why the pharmacist disagrees, in Galdino's wording.
   *
   * It used to be one checkbox -- "o sistema leu a receita errado" -- which is
   * the cause worth counting and one of four. The other three were being lost
   * as free text or not recorded at all, so the only answerable question about
   * our own accuracy was the one already asked.
   */
  const [reason, setReason] = useState<string | undefined>()
  const panel = useRef<HTMLDivElement>(null)
  const anchor = useRef<HTMLSpanElement>(null)
  const [at, setAt] = useState<{ left: number; top: number }>({ left: 0, top: 0 })

  /*
   * Dismissed by anything that is not the panel.
   *
   * A popover that only closes on its own buttons is a modal wearing a
   * disguise: the pharmacist scrolls, clicks the next row, and the thing is
   * still hanging over a different difference than the one it asked about.
   */
  /*
   * Into the top layer, and positioned against the viewport.
   *
   * An absolutely-positioned panel is clipped by the board, which is
   * `overflow-hidden` to keep its rounded corners, and again by the scrolling
   * column around it -- so the question appeared cut in half or not at all.
   * `popover` renders above every ancestor's overflow, and `fixed` coordinates
   * put it beside the button that opened it.
   */
  const openReason = () => {
    const box = anchor.current?.getBoundingClientRect()
    const sheet = panel.current
    if (box === undefined || sheet === null) return

    // Shown before it is measured: a popover has no size until it is in the
    // top layer, and reading its height first gives zero -- which would put it
    // against the top of the window rather than beside the row.
    sheet.showPopover()
    const own = sheet.getBoundingClientRect()

    const next = {
      // Right-aligned to the button, pulled back inside the window when the
      // row is near the edge.
      left: Math.max(8, Math.min(box.right - own.width, window.innerWidth - own.width - 8)),
      // Below by default; above when there is no room, which is what happens
      // on the last rows of a long preparation.
      top:
        box.bottom + own.height + 8 > window.innerHeight
          ? box.top - own.height - 6
          : box.bottom + 6,
    }
    // Written to the element as well as to status: the status lands on the next
    // render, and until then the panel is visible wherever it last was --
    // which, for a panel that was just mounted, is the window's corner.
    sheet.style.left = `${next.left}px`
    sheet.style.top = `${next.top}px`
    setAt(next)
    setAsking(true)
    // Into the question, so Tab walks the reasons and Enter answers it.
    sheet.querySelector<HTMLInputElement>('input[type="radio"]')?.focus()
  }

  const closeReason = () => {
    const sheet = panel.current
    if (sheet !== null && sheet.matches(':popover-open')) sheet.hidePopover()
    setAsking(false)
    // Back to the row the question was about, so the pass carries on from
    // where it stopped rather than from the top of the page.
    anchor.current?.closest<HTMLElement>('[data-review-row]')?.focus()
  }

  // "Outros" without a word is a disagreement with nothing in it.
  const answerable = reason !== undefined && (reason !== 'other' || notes.trim() !== '')

  const confirmReason = () => {
    if (!answerable || busy) return
    // Not `setAsking(false)`: the flag alone leaves the panel in the top
    // layer, visible over a decision already taken.
    closeReason()
    /*
     * The mark, and the answer it is.
     *
     * The override only reaches the comparison when the quotation is fetched
     * again, so this used to ask for a reverify on the spot. That is minutes
     * of model calls to hide one line -- and it throws away a conferência
     * somebody is part-way through, which cannot be rebuilt at all once the
     * ERP no longer holds the orçamento. The finding is answered here
     * instead, exactly as the other reasons answer it; the next analysis
     * applies the type for good (ADR 0014).
     */
    if (reason === EXCIPIENT.id) {
      onExcipient?.(answering.map((f) => f.fingerprint))
      return
    }
    for (const answered of answering) {
      onDecision(answered.fingerprint, NOT_A_DIVERGENCE, {
        // The term teaches the catalogue what the receita's word means, so it
        // rides only on the finding that is about that word. The others share
        // the reason, which is what the pharmacist actually said.
        // On the leading finding alone: a row answered on two dimensions
        // would otherwise propose the same term twice.
        synonymTerm:
          reason === 'synonym' && answered === finding && teaches && term.trim() !== ''
            ? term.trim()
            : undefined,
        notes: notes.trim() === '' ? undefined : notes.trim(),
        reason,
      })
    }
  }

  useEffect(() => {
    if (!asking) return undefined
    const dismiss = (event: MouseEvent) => {
      if (panel.current !== null && !panel.current.contains(event.target as Node)) {
        closeReason()
      }
    }
    // Deferred, or the click that opened it closes it in the same tick.
    const timer = setTimeout(() => document.addEventListener('mousedown', dismiss), 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', dismiss)
    }
  }, [asking])

  const fixing = recorded?.decision === WILL_FIX
  const answers = answersText(answering) ?? ''

  const undo = () => {
    for (const answered of answering) onClear(answered.fingerprint)
  }
  const agree = () => {
    const decision = quiet ? quietFor(finding).decision : WILL_FIX
    for (const answered of answering) onDecision(answered.fingerprint, decision)
  }
  const askWhy = () => {
    setTerm(teachableTerm(finding, rowName) ?? '')
    setNotes('')
    // Nothing preselected: the reason is the answer, and a default
    // would be an answer nobody gave.
    setReason(undefined)
    openReason()
  }

  return (
    /*
     * The row is itself the pin control, so anything clicked inside it reaches
     * that handler too -- answering a difference silently pinned or unpinned
     * the row it was about. Stopped here rather than on each button: every
     * click in this cell is about the decision, never about the pin.
     */
    <span
      ref={anchor}
      /*
       * Named so the guided hint can find it.
       *
       * A `data` hook rather than a ref threaded through three ingredients that
       * have no other reason to know a coach mark exists -- the card lays out
       * differences, and pointing at one is not its business.
       */
      data-coach="decision"
      className={labelled ? 'flex w-full flex-col' : 'inline-flex items-center gap-1'}
      onClick={(event) => event.stopPropagation()}
    >
      {labelled ? (
        recorded !== undefined ? (
          <LabelledAnswer
            fixing={fixing}
            recorded={recorded}
            finding={finding}
            busy={busy}
            onUndo={undo}
          />
        ) : (
          <LabelledChoices busy={busy} onAgree={agree} onDisagree={askWhy} />
        )
      ) : null}
      {/*
        The answered state, in the same element as the question.

        It used to be an early return of its own, which unmounted the reason
        panel below. Undo, and the panel came back as a *new* node; open it,
        and `showPopover()` painted it at the window's corner before it had
        ever been positioned -- Galdino's "far top-left of the page" after
        undoing twice. One element, one panel, whatever the row's status.
      */}
      {labelled ? null : recorded !== undefined ? (
        <Tooltip text={decidedText(fixing, recorded, finding) + answers}>
          <button
            type="button"
            tabIndex={-1}
            disabled={busy}
            // The recorded status is itself the undo. A separate "desfazer" would
            // need a second control in a column that has room for one thing.
            aria-label="Desfazer decisão"
            // What Z presses on the row.
            data-decision="undo"
            aria-pressed="true"
            className={`${PRESSED} ${
              fixing && !quiet
                ? 'border-stroke-success-weak bg-fill-success-weak text-text-success'
                : 'border-stroke-error-weak bg-fill-error-weak text-text-error'
            }`}
            onClick={(event) => {
              event.stopPropagation()
              undo()
            }}
          >
            <Icon name={fixing && !quiet ? 'confirm' : 'close'} />
          </button>
        </Tooltip>
      ) : (
        <>
          <Tooltip
            text={
              (quiet
                ? quietFor(finding).means
                : 'Corrigir no sistema — a divergência procede e o orçamento será corrigido.') +
              answers
            }
          >
            <button
              type="button"
              tabIndex={-1}
              data-decision={quiet ? quietFor(finding).hook : 'fix'}
              disabled={busy}
              aria-label={quiet ? quietFor(finding).label : 'Corrigir no sistema'}
              /*
               * Green on the tick, red on the cross, whatever each one means.
               *
               * The colours follow the shape rather than the semantics: a ✓ drawn
               * in red reads as a warning about the act of pressing it, and people
               * hesitate over the button that is usually the right answer. What
               * each one does is in the tooltip, where a sentence can say it
               * properly.
               *
               * A quiet row is the exception, and it is the shape that changes
               * rather than the colour. There is no tick on one because there is
               * nothing to agree with: we found nothing, and a ✓ offered against
               * our own silence reads as a decision somebody made. What is on
               * offer is disagreement, so the only mark is the cross.
               */
              className={
                quiet
                  ? `${RAISED} border-stroke-error-weak text-text-error hover:bg-fill-error-weak ${quietFor(finding).reveal}`
                  : `${RAISED} border-stroke-success-weak text-text-success hover:bg-fill-success-weak`
              }
              onClick={agree}
            >
              <Icon name={quiet ? 'close' : 'confirm'} />
            </button>
          </Tooltip>
          {!quiet && (
            <Tooltip
              text={
                'Não é divergência — o orçamento já está certo; o apontamento foi nosso erro.' +
                answers
              }
            >
              <button
                type="button"
                tabIndex={-1}
                data-decision="dismiss"
                disabled={busy}
                aria-label="Não é divergência"
                className={`${RAISED} border-stroke-error-weak text-text-error hover:bg-fill-error-weak`}
                onClick={askWhy}
              >
                <Icon name="close" />
              </button>
            </Tooltip>
          )}
        </>
      )}

      {/*
        Always mounted, never conditionally rendered.

        A panel that unmounts on close comes back as a *new* element on the
        next open, and `showPopover()` on a node that has not been positioned
        yet paints it at the popover default -- the top-left corner of the
        window. The first open worked because the element happened to be
        measured before it was seen; every one after that did not.

        Kept in the tree and toggled through the popover API instead, which is
        what `Tooltip` does and why that one never had this bug.
      */}
      <div
        ref={panel}
        role="dialog"
        aria-label="Por que não é divergência?"
        /*
         * Anchored to the row, and out of the document's flow entirely.
         *
         * Fixed coordinates rather than inline layout, so opening it cannot
         * change the row's height — the list must not reflow under somebody's
         * cursor while they are reading the difference it is about.
         */
        popover="manual"
        /*
         * No `display` utility on this element, ever.
         *
         * A closed popover is hidden by the user-agent rule
         * `[popover]:not(:popover-open) { display: none }` -- and an author
         * class beats a UA rule, so putting `flex` here made every panel on
         * the screen permanently visible. The layout lives on the child
         * instead, where it cannot fight the thing that hides this one.
         */
        className="border-stroke-weak bg-background-raised m-0 w-72 rounded-[8px] border p-3 shadow-lg"
        style={{
          position: 'fixed',
          left: at.left,
          top: at.top,
          inset: 'auto',
        }}
        /*
         * Enter anywhere in the question answers it; Escape withdraws it.
         *
         * Tab already walks the reasons and the boxes, so the whole exchange
         * is hands-on-keys: X, choose, Enter. Stopped here so the keys do not
         * reach the row underneath, where Enter means something else.
         */
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === 'Enter') {
            const target = event.target as HTMLElement
            // On a button, Enter is that button. Let it be.
            if (target.tagName === 'BUTTON') return
            event.preventDefault()
            // On a reason not yet chosen, Enter chooses it, as Space does;
            // on the chosen one -- or in a text box -- it answers.
            if (target instanceof HTMLInputElement && target.type === 'radio' && !target.checked) {
              target.click()
              return
            }
            confirmReason()
          } else if (event.key === 'Escape') {
            event.preventDefault()
            closeReason()
          } else if (event.key === 'Tab') {
            /*
             * Tab stays inside the question, going round.
             *
             * The panel is in the top layer and the browser's Tab from its
             * last control would leave for the row underneath, with the
             * question still open over it.
             */
            const sheet = panel.current
            if (sheet === null) return
            const stops = [
              ...sheet.querySelectorAll<HTMLElement>(
                'input:not([disabled]), button:not([disabled])',
              ),
            ]
            if (stops.length === 0) return
            const at = stops.indexOf(document.activeElement as HTMLElement)
            const next = event.shiftKey
              ? (stops[at <= 0 ? stops.length - 1 : at - 1] as HTMLElement)
              : (stops[at < 0 || at === stops.length - 1 ? 0 : at + 1] as HTMLElement)
            event.preventDefault()
            next.focus()
          }
        }}
      >
        <div className="flex flex-col gap-3">
          <p className="text-tSmall text-text-strong">Por que não é divergência?</p>

          {/*
            One reason, chosen rather than typed.
            
            Radios and not checkboxes: these are alternatives, and a review
            claiming the prescriber wrote it wrong *and* that it is a synonym
            says nothing anybody can count.
          */}
          {(onExcipient === undefined
            ? REASONS
            : [...REASONS.slice(0, -1), EXCIPIENT, ...REASONS.slice(-1)]
          )
            /*
             * "É um sinônimo" only where it saves one. On a posology or a
             * forma it was being picked to mean "the same thing written
             * another way", recorded, and taught nothing (#508).
             */
            .filter((option) => option.id !== 'synonym' || teaches)
            .map((option) => (
              <label key={option.id} className="text-tSmall text-text-strong flex gap-2">
                <input
                  type="radio"
                  name={`reason-${finding.fingerprint}`}
                  checked={reason === option.id}
                  onChange={() => setReason(option.id)}
                />
                {option.label}
              </label>
            ))}

          {/*
            Free text only on "Outros", and required there.
            
            It is the one option that says nothing on its own: a review reading
            `other` with an empty note records that somebody disagreed and
            nothing about why. Everywhere else the reason is the answer, and an
            optional box invites a second, contradictory one.
          */}
          {reason === 'other' && (
            <Input
              size="small"
              label="Descreva o motivo"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          )}

          {/*
            The synonym box belongs to the synonym reason.
            
            Teaching the catalogue from a misreading is how a transcription
            error becomes permanent, so the box only appears once somebody has
            said the term is right.
          */}
          {reason === 'synonym' && teaches && (
            <Input
              size="small"
              label="Guardar como sinônimo"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
          )}

          <div className="flex gap-2">
            <Button
              size="small"
              variant="primary"
              tone="brand"
              disabled={busy || !answerable}
              onClick={confirmReason}
            >
              Confirmar
            </Button>
            <Button
              size="small"
              variant="tertiary"
              tone="neutral"
              disabled={busy}
              onClick={closeReason}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </span>
  )
}

/**
 * What an answered decision says on hover: what, by whom, when.
 *
 * The label alone was a repeat of the button; the useful part of a decision
 * already taken is who took it and when, because the person reading it is
 * usually not the person who took it -- a conferência that came back from
 * comercial is exactly that case.
 */
function decidedText(fixing: boolean, recorded: Review, finding: Finding): string {
  return `${decidedWhat(fixing, finding)} — ${decidedBy(recorded)}. Clique para desfazer.`
}

function decidedWhat(fixing: boolean, finding: Finding): string {
  if (fixing) return 'Corrigir no sistema'
  return finding.check === 'operational' && finding.disposition === 'info'
    ? QUIET.reminder.label
    : 'Não é divergência'
}

function decidedBy(recorded: Review): string {
  const at = when(recorded.created_at)
  return `por ${recorded.verifier_email}${at === undefined ? '' : ` em ${at}`}`
}

/**
 * The two answers as tiles, each saying what it means (#641).
 *
 * Side by side where there is room, stacked where there is not. Each carries
 * the key that answers it: on this pass a step is one question, answered from
 * anywhere on the card. Enter and Delete are the keys shown, the ones everyone
 * already knows; A and X answer too, as on the full pass.
 */
function LabelledChoices({
  busy,
  onAgree,
  onDisagree,
}: {
  busy: boolean
  onAgree: () => void
  onDisagree: () => void
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <Choice
        decision="fix"
        tone="success"
        title="Corrigir no sistema"
        means="A divergência procede: o orçamento será corrigido."
        shortcut="Enter"
        busy={busy}
        onClick={onAgree}
      />
      <Choice
        decision="dismiss"
        tone="error"
        title="Não é divergência"
        means="O orçamento já está certo: o apontamento foi um erro nosso."
        shortcut="Delete"
        busy={busy}
        onClick={onDisagree}
      />
    </div>
  )
}

function Choice({
  decision,
  tone,
  title,
  means,
  shortcut,
  busy,
  onClick,
}: {
  decision: 'fix' | 'dismiss'
  tone: 'success' | 'error'
  title: string
  means: string
  shortcut: string
  busy: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      // The card is the stop, as a row is on the full pass; A and X press these.
      tabIndex={-1}
      data-decision={decision}
      disabled={busy}
      onClick={onClick}
      className={`rounded-base bg-fill-white flex cursor-pointer flex-col items-start gap-1 border px-3 py-2.5 text-left transition-colors disabled:cursor-default disabled:opacity-40 ${FOCUS_RING} ${
        tone === 'success'
          ? 'border-stroke-success-weak hover:bg-fill-success-weak'
          : 'border-stroke-error-weak hover:bg-fill-error-weak'
      }`}
    >
      <span className="text-tPSemi text-text-strong flex w-full items-center gap-2">
        <span className={tone === 'success' ? 'text-text-success' : 'text-text-error'}>
          <Icon name={tone === 'success' ? 'confirm' : 'close'} />
        </span>
        {title}
        <kbd className="text-tTiny text-text-weak border-stroke-weak ml-auto rounded-[4px] border px-1.5 py-0.5 font-sans">
          {shortcut}
        </kbd>
      </span>
      <span className="text-tSmall text-text-weak">{means}</span>
    </button>
  )
}

/** What was answered, by whom and when, and the way to take it back. */
function LabelledAnswer({
  fixing,
  recorded,
  finding,
  busy,
  onUndo,
}: {
  fixing: boolean
  recorded: Review
  finding: Finding
  busy: boolean
  onUndo: () => void
}) {
  const notes = recorded.notes?.trim()
  return (
    <div
      className={`rounded-base flex flex-wrap items-center gap-x-3 gap-y-1 border px-3 py-2.5 ${
        fixing
          ? 'border-stroke-success-weak bg-fill-success-weak'
          : 'border-stroke-error-weak bg-fill-error-weak'
      }`}
    >
      <span className="text-tPSemi text-text-strong flex items-center gap-2">
        <span className={fixing ? 'text-text-success' : 'text-text-error'}>
          <Icon name={fixing ? 'confirm' : 'close'} />
        </span>
        {decidedWhat(fixing, finding)}
      </span>
      <span className="text-tSmall text-text-weak min-w-0 flex-1">
        {decidedBy(recorded)}
        {notes === undefined || notes === '' ? '' : ` · “${notes}”`}
      </span>
      <Button
        size="small"
        variant="tertiary"
        tone="neutral"
        // The card is the stop; Z presses this.
        tabIndex={-1}
        data-decision="undo"
        disabled={busy}
        onClick={onUndo}
      >
        Desfazer
        <kbd className="text-tTiny text-text-weak border-stroke-weak ml-1.5 rounded-[4px] border px-1.5 py-0.5 font-sans">
          Z
        </kbd>
      </Button>
    </div>
  )
}

/** Day and time, in the order a Brazilian reads them. */
function when(iso: string | undefined): string | undefined {
  if (iso === undefined) return undefined
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return undefined
  return at.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * What one press answers, when it answers more than one thing.
 *
 * The buttons are icons and the words live in the tooltip, so a control
 * standing for two findings has to say so there or it says nothing: the row
 * looks exactly like every single-finding row beside it. Nothing when there is
 * only one — naming the obvious on every row is noise.
 */
function answersText(findings: readonly Finding[]): string | undefined {
  if (findings.length < 2) return undefined
  const names = findings
    .map((f) => CHECK_LABEL[f.check] ?? f.check)
    .filter((name, i, all) => all.indexOf(name) === i)
  if (names.length < 2) return undefined
  return ` Responde ${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}.`
}

/** Whether saying this was not a divergence says anything about what a term means. */
export function canTeach(finding: Finding, rowName?: string): boolean {
  return teachableTerm(finding, rowName) !== undefined
}
